const API_BASE: string = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(/\/$/, "");

function authHeaders(): Record<string, string> {
    const token = localStorage.getItem("token");
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
}

async function handleResponse<T>(res: Response): Promise<T> {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data?.message || `Request failed: ${res.status}`);
    }
    return data as T;
}

export type NotificationItem = {
    id: string;
    type: string;
    title: string;
    body: string;
    href: string;
    actor?: {
        username?: string;
        displayName?: string;
        avatarUrl?: string;
    } | null;
    metadata?: Record<string, any>;
    readAt?: string | null;
    createdAt: string;
};

export type NotificationsPage = {
    notifications: NotificationItem[];
    total: number;
    unreadCount: number;
    page: number;
    limit: number;
    hasMore: boolean;
    nextCursor: string | null;
};

export async function fetchNotifications(params: {
    page?: number;
    cursor?: string | null;
    limit?: number;
    unread?: boolean;
    type?: string;
    category?: string;
    q?: string;
} = {}): Promise<NotificationsPage> {
    const url = new URL(`${API_BASE}/api/notifications`);
    if (params.page) url.searchParams.set("page", String(params.page));
    if (params.cursor) url.searchParams.set("cursor", params.cursor);
    if (params.limit) url.searchParams.set("limit", String(params.limit));
    if (params.unread) url.searchParams.set("unread", "true");
    if (params.type && params.type !== "all") url.searchParams.set("type", params.type);
    if (params.category && params.category !== "all") url.searchParams.set("category", params.category);
    if (params.q?.trim()) url.searchParams.set("q", params.q.trim());
    const data = await handleResponse<any>(await fetch(url.toString(), { headers: authHeaders() }));
    return {
        notifications: data.notifications || [],
        total: data.total || 0,
        unreadCount: data.unreadCount || 0,
        page: data.page || 1,
        limit: data.limit || params.limit || 30,
        hasMore: Boolean(data.hasMore),
        nextCursor: data.nextCursor || null,
    };
}

export async function fetchNotificationSummary(): Promise<{ unreadCount: number }> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/notifications/summary`, { headers: authHeaders() })
    );
    return { unreadCount: data.unreadCount || 0 };
}

export async function markNotificationRead(id: string): Promise<{ unreadCount: number }> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/notifications/${encodeURIComponent(id)}/read`, {
            method: "PATCH",
            headers: authHeaders(),
        })
    );
    return { unreadCount: data.unreadCount || 0 };
}

export async function markNotificationsRead(ids: string[]): Promise<{ unreadCount: number; ids: string[] }> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/notifications/read-visible`, {
            method: "PATCH",
            headers: authHeaders(),
            body: JSON.stringify({ ids }),
        })
    );
    return { unreadCount: data.unreadCount || 0, ids: data.ids || ids };
}

export async function markNotificationsUnread(ids: string[]): Promise<{ unreadCount: number; ids: string[] }> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/notifications/unread-visible`, {
            method: "PATCH",
            headers: authHeaders(),
            body: JSON.stringify({ ids }),
        })
    );
    return { unreadCount: data.unreadCount || 0, ids: data.ids || ids };
}

export async function markAllNotificationsRead(): Promise<{ unreadCount: number }> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/notifications/read-all`, {
            method: "PATCH",
            headers: authHeaders(),
        })
    );
    return { unreadCount: data.unreadCount || 0 };
}

export async function deleteNotification(id: string): Promise<{ unreadCount: number }> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/notifications/${encodeURIComponent(id)}`, {
            method: "DELETE",
            headers: authHeaders(),
        })
    );
    return { unreadCount: data.unreadCount || 0 };
}

export async function deleteNotifications(ids: string[]): Promise<{ unreadCount: number; ids: string[] }> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/notifications/bulk`, {
            method: "DELETE",
            headers: authHeaders(),
            body: JSON.stringify({ ids }),
        })
    );
    return { unreadCount: data.unreadCount || 0, ids: data.ids || ids };
}
